// Auth Effects
import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, exhaustMap, catchError, tap } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { LocalCacheService } from '../../core/services/local-cache.service';
import { JwtService } from '../../core/services/jwt.service';
import { TelemetryService } from '../../core/services/telemetry.service';
import { serverErrorMessage } from '../../core/utils/server-error-message';
import * as AuthActions from './auth.actions';
import { User } from './auth.state';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private cache = inject(LocalCacheService);
  private jwt = inject(JwtService);
  private router = inject(Router);
  private telemetryService = inject(TelemetryService);

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      exhaustMap(({ email, password }) =>
        this.authService.login({ emailOrMobileNo: email, password }).pipe(
          map((response: any) => {
            const data = response?.data;
            const token = data?.token ?? '';
            const user: User = {
              id: String(data?.id ?? ''),
              email: data?.email ?? '',
              firstName: data?.firstName ?? '',
              lastName: data?.lastName ?? '',
              phoneNumber: data?.phoneNumber ?? null,
              organizationName: data?.organization?.name ?? null,
              // JWT is the source of truth for role — decode it rather than
              // trusting a possibly-absent/divergent `data.role` field.
              role: this.jwt.getRoles(token),
            };
            return AuthActions.loginSuccess({
              user,
              token,
              refreshToken: data?.refreshToken ?? '',
            });
          }),
          catchError((error) => {
            // AuthService.login already maps HTTP failures to an Error carrying the text to show.
            // A raw HttpErrorResponse that reaches here shows the server's reason or the generic
            // text, never its .message, which is Angular's "Http failure response for ..." text.
            const own = error instanceof HttpErrorResponse ? undefined : error?.message;
            const msg = serverErrorMessage(error) || own || 'Login failed';
            this.telemetryService.trackException(
              error instanceof Error ? error : new Error(msg),
              { source: 'AuthEffects:login', status: String(error?.status ?? '') }
            );
            return of(AuthActions.loginFailure({ error: msg }));
          })
        )
      )
    )
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess, AuthActions.ssoLoginSuccess),
        tap(({ user, token, refreshToken }) => {
          // Persist to session storage
          this.cache.authToken = token;
          this.cache.refreshToken = refreshToken;
          this.cache.userEmail = user.email;
          this.cache.userId = user.id;
          this.cache.firstName = user.firstName;
          this.cache.lastName = user.lastName;
          this.cache.phoneNumber = user.phoneNumber;
          this.cache.organizationName = user.organizationName;
          this.cache.displayName = `${user.firstName} ${user.lastName}`.trim();
          this.telemetryService.setUser(user.id.toString(), user.email);
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => {
        this.cache.clearAll();
        this.router.navigateByUrl('/sign-in');
      }),
      map(() => AuthActions.logoutSuccess())
    )
  );

  restoreAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.restoreAuth),
      map(() => {
        const token = this.cache.authToken;
        const refreshToken = this.cache.refreshToken;

        if (!token) {
          return AuthActions.restoreAuthFailure();
        }

        const user: User = {
          id: this.cache.userId ?? '',
          email: this.cache.userEmail ?? '',
          firstName: this.cache.firstName ?? '',
          lastName: this.cache.lastName ?? '',
          phoneNumber: this.cache.phoneNumber,
          organizationName: this.cache.organizationName,
          // Derive role from the token itself (JwtService), not the
          // separately persisted `user_role` cache entry — avoids the same
          // cache-drift issue fixed in LocalCacheService.hasAllowedRole.
          role: this.jwt.getRoles(token),
        };

        return AuthActions.restoreAuthSuccess({ user, token, refreshToken: refreshToken ?? '' });
      })
    )
  );
}
